import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import Tooltip from "rc-tooltip";
import React, { type FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Form, Grid, Loader, Segment } from "semantic-ui-react";
import { GetPlotAnalysisResultsForProtocolSummaryTableAsync } from "../../../../apis/analysisApi";
import { getMeanComparisonForTimecourseAsync, getProtocolTukeyDataAsync } from "../../../../apis/statisticsApi";
import type { ProtocolPlotAnalysisResult } from "../../../../apis/types/analysisTypes";
import type { ProtocolTreatment } from "../../../../apis/types/protocolTypes";
import type { ProtocolMeanComparisonRequestRow } from "../../../../apis/types/statisticsTypes";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import ProtocolAnalysisTopBar, { DropdownWithLabel } from "../../../Lumber/ProtocolAnalysisTopBar";
import * as tableShader from "../../../Lumber/TableShader";
import MetaTagsTable from "./MetaTagsTable";
import sharedStyles from "./ProtocolAnalysis.module.css";
import {
	type AssessmentKind,
	type ProtocolAssessmentSelection,
	useProtocolAnalysis,
	useProtocolAnalysisDispatch,
} from "./ProtocolAnalysisContext";
import ProtocolDataSummaryTable from "./ProtocolDataSummaryTable";
import ProtocolSiteSpecificSummaryTable from "./ProtocolSiteSpecificSummaryTable";
import ProtocolSummaryTableEditor from "./ProtocolSummaryTableEditor";
import { tooltipDefaults } from "./constants";
import { useProtocolDataQuery } from "./queries";
import type {
	CompiledMeanComparisonPlot,
	CompiledMeanComparisonRequest,
	FlattenedTrialMetadata,
	MeanComparisonLettersByAssessment,
	ProtocolSummaryTableProps,
	SingleTrialMeanComparison,
	SummaryAssessmentData,
	SummaryCompiledRow,
	SummarySitePlot,
	SummarySiteRow,
	SummaryTreatmentAverage,
	TreatmentCheckedState,
	TrialCheckedState,
	UpdateCheckedTreatment,
	UpdateCheckedTrial,
} from "./types";
import {
	applyPresetToConfiguredAssessments,
	applySelection,
	buildCombinedAssessmentOptions,
	buildConfiguredAssessments,
	buildMeanComparisonLetters,
	buildMeanComparisonPayload,
	buildTreatmentMatrix,
	CUSTOM_TRIAL_FILTER,
	type DataNormalizationOption,
	fromEditorAssessments,
	getAssessmentOptions,
	getDataNormalizationOptions,
	getDefaultCombinedAssessmentOption,
	getSummaryTablePresetOptions,
	type LegacyEditorAssessment,
	NO_SUMMARY_TABLE_PRESET,
	resolvePlotAnalysisValue,
	toEditorAssessments,
} from "./utils";

const PERCENT_NORMALIZATIONS = ["% mean", "% control", "% commercial", "% gsp"];

const getAssessmentValue = (row: ProtocolPlotAnalysisResult, kind: AssessmentKind) =>
	kind === "standcount"
		? row.standCountAnalysisResultValue
		: kind === "grounddata"
			? row.groundDatasetAssessmentValue
			: row.curveModelAnalysisValue;

const hasAnyValue = (row: ProtocolPlotAnalysisResult) =>
	row.groundDatasetAssessmentValue !== null ||
	row.curveModelAnalysisValue !== null ||
	row.standCountAnalysisResultValue !== null;

/** Whether a plot analysis result belongs to one configured assessment (one summary table column) */
const rowMatchesAssessment = (
	row: ProtocolPlotAnalysisResult,
	assessment: ProtocolAssessmentSelection,
	treatmentCount: number,
) =>
	//TODO(types): a row with a null trialTreatmentId passes this check, so it still feeds the
	//normalization mean and `first.*` in buildSiteData even though it never matches a treatment
	Number(row.trialTreatmentId) <= treatmentCount &&
	row.excludeFromAssessment === false &&
	(assessment.kind === "standcount"
		? row.analysisId === assessment.analysisId
		: assessment.kind === "curvemodel"
			? row.curveModelName === assessment.assessmentId
			: row.assessmentId === assessment.assessmentId && row.plannedTimingId === assessment.plannedTimingId);

/**
 * Site rows, one per configured assessment x trial x treatment, holding the treatment's normalized
 * mean and its plots. A treatment with no data in a trial gets a null average and `excluded`; when
 * every treatment in a trial failed, the whole trial is flagged `allFailed` for that assessment
 */
const buildSiteData = ({
	rows,
	configuredAssessments,
	dataNormalizationOptions,
	treatmentData,
	trialData,
	treatmentCount,
}: {
	rows: ProtocolPlotAnalysisResult[];
	configuredAssessments: ProtocolAssessmentSelection[];
	dataNormalizationOptions: DataNormalizationOption[];
	treatmentData: ProtocolTreatment[];
	trialData: FlattenedTrialMetadata[];
	treatmentCount: number;
}): SummarySiteRow[] => {
	const getNormalizationOption = (id: string | null) => _.find(dataNormalizationOptions, ["value", id]);

	const siteData: SummarySiteRow[] = [];
	_.forEach(configuredAssessments, (assessment) => {
		const normalizationOption = getNormalizationOption(assessment.dataNormalizationId);
		const normalizationName = normalizationOption?.text.toLowerCase();
		const multiplier = normalizationName !== undefined && PERCENT_NORMALIZATIONS.includes(normalizationName) ? 100 : 1;

		const getNormalizationModifier = (trialRows: ProtocolPlotAnalysisResult[]) => {
			const meanOf = (normalizationRows: ProtocolPlotAnalysisResult[]) =>
				_.meanBy(
					_.map(
						_.filter(normalizationRows, (r) => r.excluded !== true && hasAnyValue(r)),
						(r) => getAssessmentValue(r, assessment.kind),
					),
				);

			switch (normalizationName) {
				case "% mean":
					return meanOf(trialRows);
				case "% control":
				case "% commercial":
				case "% gsp": {
					//normalizationName is only set when normalizationOption is, so the `?.` never short circuits
					const trialTreatment = _.find(treatmentData, ["treatmentTypeId", normalizationOption?.treatmentTypeId]);
					return meanOf(_.filter(trialRows, ["trialTreatmentId", trialTreatment?.trialTreatmentId]));
				}
				default:
					//No modifications to none or rank order, just display raw data
					return 1;
			}
		};

		const assessmentRows = _.filter(
			rows,
			(r) =>
				rowMatchesAssessment(r, assessment, treatmentCount) &&
				(!r.failed || (r.curveModelName !== null && r.curveModelName?.includes("AUC"))),
		);

		_.forEach(_.groupBy(assessmentRows, "trialId"), (trialRows) => {
			const first = trialRows[0];
			const trialInfo = _.find(trialData, (td) => td.trialInfo.trialId === first.trialId)?.trialInfo;
			const dataNormalization = getNormalizationModifier(trialRows);
			//Number() makes the null -> 0 coercion explicit
			//TODO(types): a plot whose value for this assessment's kind is null gets an analysisValue of 0
			//(hasAnyValue only checks that *some* value column is set)
			const normalize = (value: number | null) => (Number(value) / dataNormalization) * multiplier;

			const trialSites = _.map(_.range(1, treatmentCount + 1), (trialTreatmentId): SummarySiteRow => {
				const treatmentRows = _.filter(
					trialRows,
					(r) => r.trialTreatmentId === trialTreatmentId && r.excluded !== true && hasAnyValue(r),
				);

				const plots = _.map(
					treatmentRows,
					(d): SummarySitePlot => ({
						plotReplicate: d.plotReplicate,
						plotId: d.plotId,
						fieldId: d.fieldId,
						analysisValue: normalize(getAssessmentValue(d, assessment.kind)),
						//Equal to d.trialTreatmentId (the filter above), without its nullable type
						trialTreatmentId,
						assessmentId:
							assessment.kind === "standcount"
								? d.analysisId
								: assessment.kind === "grounddata"
									? d.assessmentId
									: d.curveModelName,
						trialId: d.trialId,
						dataNormalizationId: assessment.dataNormalizationId,
					}),
				);

				return {
					trialId: first.trialId,
					trialName: first.trialName,
					cooperatorName: trialInfo?.cooperatorName,
					trialTreatmentId,
					assessmentId: assessment.assessmentId,
					assessmentName: assessment.assessmentName,
					kind: assessment.kind,
					//TODO(types): ProtocolPlotAnalysisResult has no unitName (the backend view model drops it),
					//so this is always undefined
					unitName: undefined,
					plannedTimingId: assessment.plannedTimingId,
					//A null average marks a treatment with no data in this trial; the tables filter on it
					average: plots.length > 0 ? _.meanBy(plots, "analysisValue") : null,
					excluded: plots.length === 0,
					region: first.region,
					country: first.country,
					countryCode: first.countryCode,
					city: first.city,
					fieldId: first.fieldId,
					plots,
					trialDataQualityId: trialInfo?.trialDataQualityId,
					trialExclusionStatusId: trialInfo?.trialExclusionStatusId,
					trialSiteResponseId: trialInfo?.trialSiteResponseId,
				};
			});

			const allFailed = _.every(trialSites, "excluded");
			siteData.push(...(allFailed ? _.map(trialSites, (s) => ({ ...s, allFailed: true as const })) : trialSites));
		});
	});
	return siteData;
};

/**
 * Gives each compiled treatment row its per-column averages, then ranks (for rank order) and shades
 * every column across the checked treatments. The data summary table reads `treatmentAverages` off
 * these rows, so they have to be on every row, not just the checked ones
 */
const addTreatmentAverages = (
	rows: Omit<SummaryCompiledRow, "treatmentAverages">[],
	columns: ProtocolAssessmentSelection[],
): SummaryCompiledRow[] => {
	const averageAt = (assessmentData: SummaryAssessmentData[], column: ProtocolAssessmentSelection) =>
		_.meanBy(
			_.filter(
				assessmentData,
				(ad) =>
					ad.assessmentId == column.assessmentId &&
					ad.plannedTimingId == column.plannedTimingId &&
					ad.plots?.length > 0,
			),
			"assessmentAverage",
		);

	const withAverages = _.map(rows, (row) => ({
		...row,
		treatmentAverages: _.map(
			columns,
			(column): SummaryTreatmentAverage => ({
				assessmentId: column.assessmentId,
				plannedTimingId: column.plannedTimingId,
				average: averageAt(row.assessmentData, column),
				averageForStrikeThrough: averageAt(row.assessmentDataForStrikeThrough, column),
				reverseTableShading: column.assessmentName.includes("MG50"),
			}),
		),
	}));

	//The averages were just built above, so ranking and shading them in place is safe
	const checkedRows = _.filter(withAverages, "treatmentChecked");
	_.forEach(columns, (column, index) => {
		const columnAverages = _.filter(
			_.map(checkedRows, (row) => row.treatmentAverages[index]),
			(ta) => !isNaN(ta.average),
		);

		if (column.dataNormalizationName?.toLowerCase() === "rank order") {
			_.forEach(_.orderBy(columnAverages, ["average"], ["desc"]), (ta, rank) => {
				ta.average = rank + 1;
				ta.reverseTableShading = true;
			});
		}

		if (columnAverages[0]?.reverseTableShading) {
			tableShader.calculateShadingForTreatmentReversed(columnAverages, "average", "236, 196, 76");
		} else {
			tableShader.calculateShadingForTreatment(columnAverages, "average", "236, 196, 76");
		}
	});

	return withAverages;
};

const ProtocolSummaryTable: FC<ProtocolSummaryTableProps> = ({
	dashboardOptions,
	standCountAnalyses,
	treatmentData,
	trialMetadataResponse,
	metadataLoading,
}) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const moduleNavigation = useModuleNavigation();

	const { committed, pending, display, isDirty, isInitialized, isLettersOutdated } = useProtocolAnalysis();
	const { changeSettings, changeSettingsSilently, initializeSettings, changeDisplaySettings } =
		useProtocolAnalysisDispatch();

	const trialIds = dashboardOptions.trialIds;
	const presetData = dashboardOptions.summaryTablePresets;

	//Data Sources
	//index.tsx only mounts this page once the protocol has loaded, so this reads the cached copy
	const { data: protocolDataResponse } = useProtocolDataQuery({ refetchOnMount: false });
	const protocolData = protocolDataResponse?.protocolData ?? null;
	const trialData = trialMetadataResponse;

	//UI Control
	const [openModalEditor, setOpenModalEditor] = useState(false);
	//The site specific table's assessment picker only changes what is shown, so it stays local
	const [selectedCombinedAssessmentKey, setSelectedCombinedAssessmentKey] = useState<string | null>(null);

	//Dropdown options
	//Stand count is not in dashboardOptions; it comes from the client's analyses, which load separately
	const assessmentOptions = useMemo(
		() => getAssessmentOptions(dashboardOptions, standCountAnalyses),
		[dashboardOptions, standCountAnalyses],
	);
	//undefined while the analyses are loading. Only matters when the trials have stand counts
	const standCountAnalysesReady = !dashboardOptions.trialsContainStandCounts || standCountAnalyses !== undefined;

	const dataNormalizationOptions = useMemo(
		() => getDataNormalizationOptions(dashboardOptions.dataNormalizations, treatmentData),
		[dashboardOptions, treatmentData],
	);

	const presetOptions = useMemo(() => getSummaryTablePresetOptions(presetData), [presetData]);

	const combinedAssessmentOptions = useMemo(
		() => buildCombinedAssessmentOptions(pending.configuredAssessments),
		[pending.configuredAssessments],
	);

	const selectedCombinedAssessmentOption = useMemo(
		() =>
			_.find(combinedAssessmentOptions, ["key", selectedCombinedAssessmentKey]) ??
			getDefaultCombinedAssessmentOption(combinedAssessmentOptions),
		[combinedAssessmentOptions, selectedCombinedAssessmentKey],
	);

	const selectedCombinedAssessment = useMemo(
		() =>
			selectedCombinedAssessmentOption
				? //_.isMatch because lodash cannot type the object shorthand against a union
					_.find(pending.configuredAssessments, (s) =>
						_.isMatch(s, {
							assessmentId: selectedCombinedAssessmentOption.assessmentId,
							plannedTimingId: selectedCombinedAssessmentOption.plannedTimingId,
							analysisTypeId: selectedCombinedAssessmentOption.analysisTypeId,
						}),
					)
				: undefined,
		[pending.configuredAssessments, selectedCombinedAssessmentOption],
	);

	const checkedAssessments = useMemo(
		() => _.filter(pending.configuredAssessments, "checked"),
		[pending.configuredAssessments],
	);

	//The editor resets its working copy whenever this changes identity, so it has to be memoized
	const editorAssessments = useMemo(
		() => toEditorAssessments(pending.configuredAssessments),
		[pending.configuredAssessments],
	);

	//Treatment and trial selection
	//Unchecked treatments are greyed out and emptied until the selection is committed
	const hideUncheckedTreatmentData = useMemo(() => {
		if (!treatmentData) return false;

		const fullMatrix = buildTreatmentMatrix(treatmentData);
		return !_.isEqual(committed.selectedTreatmentMatrix ?? fullMatrix, pending.selectedTreatmentMatrix ?? fullMatrix);
	}, [treatmentData, committed.selectedTreatmentMatrix, pending.selectedTreatmentMatrix]);

	//The letters are stale whenever anything that feeds them has a pending change
	const hideLetters = isDirty || isLettersOutdated;

	const treatmentsChecked = useMemo((): TreatmentCheckedState[] | null => {
		if (!treatmentData) return null;

		const selectedTreatmentMatrix = pending.selectedTreatmentMatrix;
		return _.map(treatmentData, (td) => ({
			trialTreatmentId: td.trialTreatmentId,
			treatmentChecked: selectedTreatmentMatrix ? !!selectedTreatmentMatrix[td.trialTreatmentId] : true,
		}));
	}, [treatmentData, pending.selectedTreatmentMatrix]);

	const trialsChecked = useMemo((): TrialCheckedState[] | null => {
		if (!trialIds) return null;

		const selectedTrialIds = pending.selectedTrialIds;
		return _.map(trialIds, (t) => ({
			trialId: t,
			trialChecked: selectedTrialIds ? selectedTrialIds.includes(t) : true,
		}));
	}, [trialIds, pending.selectedTrialIds]);

	//Assessment data
	const standCountAnalysisId = _.find(committed.configuredAssessments, ["kind", "standcount"])?.analysisId ?? "";

	const {
		data: rawTrialData,
		isFetching: loading,
		isSuccess: rawTrialDataLoaded,
	} = useQuery({
		queryKey: [
			"protocol summary table data",
			userAuth.currentClientId,
			moduleNavigation.protocolId,
			committed.removeOutliers,
			committed.removeDamagedOrExcluded,
			committed.outlierType,
			standCountAnalysisId,
		],
		queryFn: async () =>
			GetPlotAnalysisResultsForProtocolSummaryTableAsync(
				//`enabled` requires both ids
				userAuth.currentClientId!,
				moduleNavigation.protocolId!,
				await getTokenSilently(),
				committed.removeOutliers,
				committed.removeDamagedOrExcluded,
				committed.outlierType,
				standCountAnalysisId,
			),
		select: (data) => data.data,
		//Waits for the columns, since a stand count column changes the request
		enabled: !!userAuth.currentClientId && !!moduleNavigation.protocolId && committed.configuredAssessments.length > 0,
	});

	const siteDataInputs = useMemo(
		() =>
			rawTrialData && trialData && protocolData
				? {
						rows: rawTrialData,
						dataNormalizationOptions,
						treatmentData,
						trialData,
						treatmentCount: protocolData.treatments,
					}
				: null,
		[rawTrialData, trialData, protocolData, dataNormalizationOptions, treatmentData],
	);

	//Built from the pending assessments, so the tables follow the editor right away
	const siteSpecificData = useMemo(
		() =>
			siteDataInputs
				? buildSiteData({ ...siteDataInputs, configuredAssessments: pending.configuredAssessments })
				: null,
		[siteDataInputs, pending.configuredAssessments],
	);

	//The mean comparisons run on the committed assessments (their normalization changes the values).
	//After a commit both point at the same array, so this only builds twice while the editor is dirty
	const committedSiteSpecificData = useMemo(() => {
		if (committed.configuredAssessments === pending.configuredAssessments) return siteSpecificData;

		return siteDataInputs
			? buildSiteData({ ...siteDataInputs, configuredAssessments: committed.configuredAssessments })
			: null;
	}, [siteDataInputs, siteSpecificData, committed.configuredAssessments, pending.configuredAssessments]);

	const pendingSiteSpecificData = useMemo(
		() => applySelection(siteSpecificData, pending.selectedTreatmentMatrix, pending.selectedTrialIds),
		[siteSpecificData, pending.selectedTreatmentMatrix, pending.selectedTrialIds],
	);

	//Feeds the data summary table, one row per treatment
	const compiledData = useMemo(() => {
		if (!pendingSiteSpecificData?.length || !treatmentData || !protocolData) return null;

		//A treatment is flagged when it is missing from a trial, or has an excluded (but not wholly
		//failed) site among the checked assessments and trials
		const checkedAssessmentIds = _.map(checkedAssessments, "assessmentId");
		const sitesByTreatment = _.groupBy(pendingSiteSpecificData, "trialTreatmentId");
		const maxSites = _.max(_.map(sitesByTreatment, (sites) => sites.length));
		const treatmentsMissingInTrials = _.map(
			_.filter(
				_.values(sitesByTreatment),
				(sites) =>
					sites.length !== maxSites ||
					_.some(
						sites,
						(s) => checkedAssessmentIds.includes(s.assessmentId) && s.trialChecked && s.excluded && !s.allFailed,
					),
			),
			(sites) => sites[0].trialTreatmentId,
		);

		const toAssessmentData = (s: SummarySiteRow): SummaryAssessmentData => ({
			fieldId: s.fieldId,
			assessmentId: s.assessmentId,
			plannedTimingId: s.plannedTimingId, //this will be null for all growth curve data
			assessmentAverage: s.average,
			unitName: s.unitName,
			plots: s.plots,
		});

		const rows = _.map(
			_.range(1, protocolData.treatments + 1),
			(trialTreatmentId): Omit<SummaryCompiledRow, "treatmentAverages"> => {
				const treatment = _.find(treatmentData, { trialTreatmentId });
				const checkedTrialSites = _.filter(
					pendingSiteSpecificData,
					(s) => s.trialTreatmentId === trialTreatmentId && s.trialChecked && s.plots.length > 0,
				);

				return {
					trialTreatmentId,
					treatmentTypeName: treatment?.treatmentTypeName,
					treatmentName: treatment?.name,
					assessmentData: _.map(_.filter(checkedTrialSites, "treatmentChecked"), toAssessmentData),
					assessmentDataForStrikeThrough: _.map(checkedTrialSites, toAssessmentData),
					treatmentChecked: pending.selectedTreatmentMatrix
						? !!pending.selectedTreatmentMatrix[trialTreatmentId]
						: true,
					trialId: sitesByTreatment[trialTreatmentId]?.[0]?.trialId,
					hasMissingTreatments: treatmentsMissingInTrials.includes(trialTreatmentId),
				};
			},
		);

		return addTreatmentAverages(rows, checkedAssessments);
	}, [pendingSiteSpecificData, treatmentData, protocolData, checkedAssessments, pending.selectedTreatmentMatrix]);

	//What the site specific table shows: the picked assessment, with the pending selection
	const filteredSiteSpecificData = useMemo(() => {
		const option = selectedCombinedAssessmentOption;
		if (!option) return [];

		return _.filter(
			pendingSiteSpecificData,
			(ssd) =>
				ssd.assessmentId === option.assessmentId &&
				(ssd.kind !== "grounddata" || ssd.plannedTimingId === option.plannedTimingId),
		);
	}, [pendingSiteSpecificData, selectedCombinedAssessmentOption]);

	const trialInfo = useMemo(() => {
		const trialIdsWithData = _.map(
			_.uniqBy(
				_.filter(filteredSiteSpecificData, (s) => s.plots.length > 0),
				"trialId",
			),
			"trialId",
		);

		return _.sortBy(
			_.map(
				_.filter(trialData, (td) => trialIdsWithData.includes(td.trialInfo.trialId)),
				(td) => td.trialInfo,
			),
			["farmCountry", "farmRegion", "farmCity", "trialName", "cooperatorName", "trialId"],
		);
	}, [trialData, filteredSiteSpecificData]);

	//Mean comparisons
	//Single trial letters for the site specific table's assessment, from the committed treatments
	//HACK: since we are hijacking, the prop names are not gonna add up
	const aucMeanComparisonRequestData = useMemo(() => {
		const assessment = selectedCombinedAssessment;
		const treatmentCount = protocolData?.treatments ?? 0;
		const matrix = committed.selectedTreatmentMatrix;

		const rows = assessment
			? _.filter(
					rawTrialData,
					(r) =>
						rowMatchesAssessment(r, assessment, treatmentCount) &&
						(assessment.kind === "grounddata" || r.analysisTypeId === assessment.analysisTypeId) &&
						//A null id has no matrix entry, so it is excluded
						(matrix ? r.trialTreatmentId !== null && !!matrix[r.trialTreatmentId] : true),
				)
			: [];

		//A zero is treated as missing, like the rest of this page's AUC values
		return buildMeanComparisonPayload(
			rows,
			(r) => resolvePlotAnalysisValue(r) || null,
			//TODO(types): trialTreatmentId and plotReplicate are nullable on the API rows but the payload
			//requires numbers; nulls are sent as they are
			(r) =>
				({ trialId: r.trialId, trialTreatmentId: r.trialTreatmentId, plotReplicate: r.plotReplicate }) as {
					trialId: string;
					trialTreatmentId: number;
					plotReplicate: number;
				},
		);
	}, [rawTrialData, selectedCombinedAssessment, protocolData, committed.selectedTreatmentMatrix]);

	const { data: singleTrialAnovaValues, isFetching: singleTrialTukeyRunning } = useQuery({
		queryKey: [
			"trial mean comparison data",
			aucMeanComparisonRequestData,
			committed.removeDamagedOrExcluded,
			committed.alphaOptionId,
			committed.meanComparisonOptionId,
			committed.removeOutliers,
		],
		queryFn: async () =>
			getMeanComparisonForTimecourseAsync(
				aucMeanComparisonRequestData,
				//TODO(types): `enabled` does not check the client id, so a null one is sent as "null"
				userAuth.currentClientId as string,
				"00000000-0000-0000-0000-000000000000",
				//`enabled` requires both ids
				committed.meanComparisonOptionId!,
				committed.alphaOptionId!,
				await getTokenSilently(),
				committed.removeDamagedOrExcluded,
				committed.removeOutliers,
				committed.outlierType,
			),
		select: ({ data }): SingleTrialMeanComparison[] => {
			//TODO(types): `data` is a reason string when the endpoint rejects the request; the typeof
			//check treats it as no letters
			const res = _.map(typeof data === "string" ? undefined : data.letters, (trial): SingleTrialMeanComparison => {
				return {
					trialId: trial.flightId,
					letteringPairs: _.map(trial.treatments, (trt) => {
						return { ...trt, trialId: trial.flightId, tukeyLetters: trt.letter, treatmentId: trt.trialTreatmentId };
					}),
					pValue: trial.treatments[0].pValue,
					cv: trial.treatments[0].cv,
					mean: trial.treatments[0].mean,
				};
			});

			return _.filter(res, "letteringPairs");
		},
		enabled:
			(aucMeanComparisonRequestData.data?.length ?? 0) > 0 &&
			!!committed.alphaOptionId &&
			!!committed.meanComparisonOptionId,
	});

	const singleTrialTukeyData = useMemo(
		() => _.flatMap(singleTrialAnovaValues, (data) => data.letteringPairs),
		[singleTrialAnovaValues],
	);

	//The compiled mean comparison request, one group per assessment/planned timing, built entirely
	//from committed settings so ticking a box cannot move it
	const compiledMeanComparisonRequest = useMemo((): CompiledMeanComparisonRequest<SummarySitePlot> => {
		const committedCheckedAssessments = _.filter(committed.configuredAssessments, "checked");
		const sites = applySelection(
			committedSiteSpecificData,
			committed.selectedTreatmentMatrix,
			committed.selectedTrialIds,
		);

		const plotData = _.orderBy(
			_.flatMap(
				_.filter(
					sites,
					(s) =>
						s.treatmentChecked &&
						s.trialChecked &&
						s.plots.length > 0 &&
						_.some(committedCheckedAssessments, { assessmentId: s.assessmentId, plannedTimingId: s.plannedTimingId }),
				),
				(s) =>
					_.map(
						s.plots,
						(p): CompiledMeanComparisonPlot<SummarySitePlot> => ({ ...p, plannedTimingId: s.plannedTimingId }),
					),
			),
			["assessmentId", "plannedTimingId", "trialId", "trialTreatmentId", "plotReplicate"],
		);

		return {
			groups: _.values(_.groupBy(plotData, (pd) => `${pd.assessmentId} - ${pd.plannedTimingId}`)),
			hasMultipleTrials: _.uniqBy(plotData, "trialId").length > 1,
		};
	}, [
		committedSiteSpecificData,
		committed.configuredAssessments,
		committed.selectedTreatmentMatrix,
		committed.selectedTrialIds,
	]);

	const {
		data: compiledTukeyData,
		isFetching: compiledTukeyRunning,
		isError: compiledTukeyFailed,
		isSuccess: compiledTukeySucceeded,
	} = useQuery({
		queryKey: [
			"protocol summary compiled mean comparison data",
			moduleNavigation.protocolId,
			userAuth.currentClientId,
			compiledMeanComparisonRequest.groups,
			committed.alphaOptionId,
			committed.meanComparisonOptionId,
		],
		queryFn: async () => {
			const accessToken = await getTokenSilently();

			//The endpoint takes one assessment/planned timing group at a time
			return Promise.all(
				_.map(compiledMeanComparisonRequest.groups, (plotData) =>
					getProtocolTukeyDataAsync(
						//TODO(types): plotReplicate can be null and dataNormalizationId null, but the request row
						//requires a number / string; the plots are sent as they are
						plotData as ProtocolMeanComparisonRequestRow[],
						//`enabled` requires all three ids
						committed.alphaOptionId!,
						committed.meanComparisonOptionId!,
						userAuth.currentClientId!,
						accessToken,
					),
				),
			);
		},
		//TODO(types): the letters are grouped by assessmentId only, so a ground data assessment at two
		//growth phases puts both phases' letters under one key and the columns collide
		select: (responses): MeanComparisonLettersByAssessment =>
			buildMeanComparisonLetters(_.flatMap(responses, (r) => r.data?.protocolData ?? [])),
		enabled:
			compiledMeanComparisonRequest.groups.length > 0 &&
			compiledMeanComparisonRequest.hasMultipleTrials &&
			!!committed.alphaOptionId &&
			!!committed.meanComparisonOptionId &&
			!!userAuth.currentClientId,
	});

	const oneTrialDatasetError =
		compiledMeanComparisonRequest.groups.length > 0 && !compiledMeanComparisonRequest.hasMultipleTrials;
	const tukeyRunning = singleTrialTukeyRunning || compiledTukeyRunning;

	//Outcome reporting for the compiled run
	useEffect(() => {
		if (compiledTukeyRunning) {
			return;
		}

		if (oneTrialDatasetError) {
			//Adding a toast id so that we do not get multiples of the same messages
			toast.error("Cannot run multi-trial mean comparisons when only 1 trial dataset is available", {
				toastId: "OneTrialDatasetErrorSend",
			});
		} else if (compiledTukeyFailed) {
			toast.error("There was an issue getting mean comparison values for the multi-trial datasets", {
				toastId: "MultiTrialDatasetErrorSend",
			});
		} else if (!compiledTukeySucceeded) {
			//Nothing has been asked of the endpoint yet
			return;
		}

		toast.success("All data has finished loading.", { toastId: "allFinished" });
	}, [oneTrialDatasetError, compiledTukeyRunning, compiledTukeyFailed, compiledTukeySucceeded]);

	//Page lifecycle
	//Seeds the assessment columns from the default preset. Waits for the top bar to seed the
	//statistics defaults first, since both go through initializeSettings, and for the stand count
	//analysis, since columns are only seeded once. Skipped when the context already has columns,
	//e.g. when coming back to this tab
	useEffect(() => {
		if (!isInitialized || !standCountAnalysesReady || committed.configuredAssessments.length > 0) return;

		const configuredAssessments = buildConfiguredAssessments(
			dashboardOptions,
			assessmentOptions,
			dataNormalizationOptions,
		);
		if (configuredAssessments.length === 0) return;

		const defaultPreset = _.find(presetData, { default: true });
		initializeSettings({
			summaryTablePresetId: defaultPreset?.id ?? NO_SUMMARY_TABLE_PRESET,
			configuredAssessments: applyPresetToConfiguredAssessments(configuredAssessments, defaultPreset),
		});
	}, [
		isInitialized,
		standCountAnalysesReady,
		committed.configuredAssessments,
		dashboardOptions,
		assessmentOptions,
		dataNormalizationOptions,
		presetData,
		initializeSettings,
	]);

	useEffect(() => {
		if (trialData && !!display.trialFilterId && display.trialFilterId !== CUSTOM_TRIAL_FILTER) {
			const passingTrialIds = _.map(
				_.filter(
					trialData,
					({ trialInfo }) =>
						!display.trialFilters.status.includes(trialInfo.trialExclusionStatusId) &&
						!display.trialFilters.response.includes(trialInfo.trialSiteResponseId) &&
						!display.trialFilters.quality.includes(trialInfo.trialDataQualityId),
				),
				(td) => td.trialInfo.trialId,
			);
			if (committed.selectedTrialIds === null) {
				//Bypasses committed vs pending
				initializeSettings({ selectedTrialIds: passingTrialIds });
			} else {
				changeSettingsSilently({ selectedTrialIds: passingTrialIds });
			}
		}
	}, [
		display.trialFilterId,
		display.trialFilters,
		trialData,
		committed.selectedTrialIds,
		changeSettingsSilently,
		initializeSettings,
	]);

	//Handlers
	//Both handlers take one id or a list and dispatch once
	//Note that each call reads `pending` from this render,
	//so calling them in a loop would leave only the last change standing
	const updateCheckedTreatment: UpdateCheckedTreatment = (value, trialTreatmentIdOrIds) => {
		const matrix = [...(pending.selectedTreatmentMatrix ?? buildTreatmentMatrix(treatmentData))];
		for (const trialTreatmentId of _.castArray(trialTreatmentIdOrIds)) {
			matrix[trialTreatmentId] = value;
		}
		changeSettings({ selectedTreatmentMatrix: matrix });
	};

	const updateTrialsChecked: UpdateCheckedTrial = (value, trialIdOrIds, silent = false) => {
		if (!silent) changeDisplaySettings({ trialFilterId: CUSTOM_TRIAL_FILTER });
		const ids = _.castArray(trialIdOrIds);
		const current = pending.selectedTrialIds ?? trialIds;
		changeSettings({ selectedTrialIds: value ? _.union(current, ids) : _.difference(current, ids) });
	};

	//"None" means the user is hand-picking, so it leaves the columns as they are
	function handlePresetChange(presetId: string) {
		changeSettings({
			summaryTablePresetId: presetId,
			configuredAssessments: applyPresetToConfiguredAssessments(
				pending.configuredAssessments,
				_.find(presetData, { id: presetId }),
			),
		});
	}

	function onApplySummaryTableEditor(
		assessmentEdits: LegacyEditorAssessment[],
		assessmentsPresetSelectionEdit: string | undefined,
	) {
		changeSettings({
			configuredAssessments: fromEditorAssessments(assessmentEdits, pending.configuredAssessments),
			summaryTablePresetId: assessmentsPresetSelectionEdit ?? pending.summaryTablePresetId,
		});
		setOpenModalEditor(false);

		toast.warn("Assessments updated. Please click the Update Analytics button.");
	}

	//Only once the columns are seeded, or an unseeded page would briefly read as empty
	const noData =
		rawTrialDataLoaded &&
		pending.configuredAssessments.length > 0 &&
		siteSpecificData !== null &&
		siteSpecificData.length === 0;

	return noData ? (
		<Segment basic>
			<p>{"We could not find data for this protocol."}</p>
		</Segment>
	) : (
		<Segment basic style={{ padding: 0 }}>
			<Form>
				<ProtocolAnalysisTopBar
					mode="summaryTable"
					dashboardOptions={dashboardOptions}
					standCountAnalyses={standCountAnalyses}
					treatments={treatmentData}
					loading={loading}
					disabled={tukeyRunning || metadataLoading}
					leadingControls={
						<>
							<Tooltip {...tooltipDefaults} overlay="Change settings of protocol analysis summary table">
								<Button
									style={{ alignSelf: "flex-end" }}
									icon="settings"
									onClick={() => setOpenModalEditor(true)}
									disabled={tukeyRunning || loading}
								/>
							</Tooltip>
							<DropdownWithLabel
								label="Assessment Presets"
								options={presetOptions}
								disabled={tukeyRunning || loading}
								value={pending.summaryTablePresetId ?? NO_SUMMARY_TABLE_PRESET}
								//semantic-ui types a dropdown value loosely; every preset option's value is a string
								onChange={(_event, { value }) => handlePresetChange(value as string)}
							/>
						</>
					}
				/>
				<ProtocolSummaryTableEditor
					open={openModalEditor}
					assessmentOptions={editorAssessments}
					presetData={presetData}
					dataNormalizationOptions={dataNormalizationOptions}
					assessmentPresetOptions={presetOptions}
					selectedAssessmentPresetId={pending.summaryTablePresetId ?? NO_SUMMARY_TABLE_PRESET}
					onCancel={() => setOpenModalEditor(false)}
					onApply={onApplySummaryTableEditor}
				/>
				{loading || metadataLoading || !compiledData ? (
					<Grid.Row>
						<Grid.Column width="16">
							<Loader active></Loader>
						</Grid.Column>
					</Grid.Row>
				) : (
					<Grid>
						{checkedAssessments.length > 0 ? (
							<Grid.Row centered style={{ paddingTop: "1em" }}>
								<Grid.Column>
									<Form.Field className="aucTableContainer" style={{ overflow: "auto", flexWrap: "nowrap" }}>
										<ProtocolDataSummaryTable
											compiledData={compiledData}
											tukeyData={compiledTukeyData ?? {}}
											selectedAssessmentOptions={checkedAssessments}
											updateCheckedTreatment={updateCheckedTreatment}
											treatmentsChecked={treatmentsChecked}
											disableCheckBoxes={tukeyRunning}
											compiledMeanComparisonsRunning={compiledTukeyRunning}
											selectedAssessmentPresetOption={pending.summaryTablePresetId}
											presetData={presetData}
											trialsChecked={trialsChecked}
											hideUncheckedTreatmentData={hideUncheckedTreatmentData}
											hideLetters={hideLetters}
											collapseView={display.collapseView}
										/>
									</Form.Field>
									<br style={{ clear: "both" }} />
								</Grid.Column>
							</Grid.Row>
						) : (
							<Segment style={{ marginTop: 15, marginBottom: 15, width: "100%" }} textAlign="center">
								<h2>{"No assessments selected."}</h2>
							</Segment>
						)}
						<hr style={{ width: "100%" }} />
						<Grid.Row centered style={{ textAlign: "center" }}>
							<h2>Site-Specific Data</h2>
						</Grid.Row>
						<Grid.Row centered style={{ textAlign: "center" }}>
							<Grid.Column width="3" style={{ width: "25%" }}>
								<Form.Select
									id="form-data-normalization"
									fluid
									options={combinedAssessmentOptions}
									disabled={tukeyRunning}
									//semantic-ui types a dropdown value loosely; every combined option's value is its string key
									onChange={(event, { value }) => setSelectedCombinedAssessmentKey(value as string)}
									value={selectedCombinedAssessmentOption?.key}
								/>
							</Grid.Column>
						</Grid.Row>
						{(siteSpecificData?.length ?? 0) > 0 && trialData && (
							<div className={sharedStyles.trialTableWrapper}>
								<Grid.Row className="aucTableContainer" style={{ padding: 0 }}>
									<MetaTagsTable trialInfo={trialInfo} collapseView={display.collapseView} />
								</Grid.Row>
								<Grid.Row className="aucTableContainer" style={{ padding: 0 }}>
									<ProtocolSiteSpecificSummaryTable
										siteSpecificData={filteredSiteSpecificData}
										updateCheckedTrial={updateTrialsChecked}
										appendedPercent={!!selectedCombinedAssessment?.dataNormalizationName?.includes("%")}
										disableCheckBoxes={tukeyRunning}
										tukeyData={singleTrialTukeyData}
										tukeyDataRunning={tukeyRunning}
										treatmentsChecked={treatmentsChecked}
										hideUncheckedTreatmentData={hideUncheckedTreatmentData}
										//TODO(types): ProtocolSiteSpecificSummaryTable never reads hideLetters
										hideLetters={hideLetters}
										collapseView={display.collapseView}
									/>
								</Grid.Row>
							</div>
						)}
					</Grid>
				)}
			</Form>
		</Segment>
	);
};

export default ProtocolSummaryTable;
